import { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { navLinks } from '@/mocks/homeData';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const dropdownTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Scroll to the hash target after navigating to a page section
  useEffect(() => {
    if (!location.hash) return;
    const timer = setTimeout(() => {
      document.querySelector(location.hash)?.scrollIntoView({ behavior: 'smooth' });
    }, 200);
    return () => clearTimeout(timer);
  }, [location.hash, location.pathname]);

  const handleDropdownEnter = (label: string) => {
    if (dropdownTimeoutRef.current) clearTimeout(dropdownTimeoutRef.current);
    setActiveDropdown(label);
  };

  const handleDropdownLeave = () => {
    dropdownTimeoutRef.current = setTimeout(() => {
      setActiveDropdown(null);
    }, 150);
  };

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    if (href.startsWith('#')) {
      e.preventDefault();
      const target = document.querySelector(href);
      if (target) {
        target.scrollIntoView({ behavior: 'smooth' });
      } else {
        navigate(`/${href}`);
      }
      setMobileOpen(false);
      setActiveDropdown(null);
    }
  };

  const isActive = (href: string) => href.startsWith('/') && location.pathname === href;

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? 'bg-background-50/30 backdrop-blur-xl border-b border-background-200/60 shadow-sm'
          : 'bg-background-50 backdrop-blur-xl'
      }`}
    >
      <div className="w-full px-4 md:px-8 lg:px-12">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 shrink-0">
            <div className="w-9 h-9 md:w-10 md:h-10 rounded-lg bg-primary-500 flex items-center justify-center">
              <i className="ri-fingerprint-line text-background-50 text-lg md:text-xl" />
            </div>
            <div className="flex flex-col">
              <span className="text-lg md:text-xl font-bold tracking-tight text-foreground-950 font-heading leading-tight">
                Ardeun
              </span>
              <span className="text-[10px] md:text-xs text-foreground-600 leading-tight -mt-0.5">
                Biometrics Corporation
              </span>
            </div>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => (
              <div
                key={link.label}
                className="relative"
                onMouseEnter={() => handleDropdownEnter(link.label)}
                onMouseLeave={handleDropdownLeave}
              >
                {link.href.startsWith('/') ? (
                  <Link
                    to={link.href}
                    className={`flex items-center gap-1 px-3 py-2 text-sm font-medium transition-colors rounded-md whitespace-nowrap ${
                      isActive(link.href)
                        ? 'text-primary-700'
                        : 'text-foreground-700 hover:text-foreground-950'
                    }`}
                  >
                    {link.label}
                  </Link>
                ) : (
                  <a
                    href={link.href}
                    onClick={(e) => handleNavClick(e, link.href)}
                    className="flex items-center gap-1 px-3 py-2 text-sm font-medium text-foreground-700 hover:text-foreground-950 transition-colors rounded-md whitespace-nowrap"
                  >
                    {link.label}
                    {link.children && (
                      <i className="ri-arrow-down-s-line text-xs transition-transform duration-200" />
                    )}
                  </a>
                )}
                {link.children && activeDropdown === link.label && (
                  <div className="absolute top-full left-0 mt-1 w-56 bg-background-50/95 backdrop-blur-xl rounded-xl border border-background-200/70 shadow-lg overflow-hidden animate-fade-in-down">
                    {link.children.map((child) => (
                      <a
                        key={child.label}
                        href={child.href}
                        className="block px-4 py-2.5 text-sm text-foreground-700 hover:text-foreground-950 hover:bg-background-100/80 transition-colors"
                      >
                        {child.label}
                      </a>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* CTA + Mobile Toggle */}
          <div className="flex items-center gap-3">
            <a
              href="#contact"
              onClick={(e) => handleNavClick(e, '#contact')}
              className="hidden md:inline-flex items-center gap-1.5 px-5 py-2.5 bg-foreground-900 text-background-50 text-sm font-medium rounded-full hover:bg-foreground-800 transition-colors whitespace-nowrap"
            >
              Request a Contact
              <i className="ri-arrow-right-line text-sm" />
            </a>
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="lg:hidden w-10 h-10 flex items-center justify-center rounded-lg hover:bg-background-100 transition-colors"
              aria-label="Toggle menu"
            >
              <i className={`${mobileOpen ? 'ri-close-line' : 'ri-menu-line'} text-xl text-foreground-900`} />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="lg:hidden bg-background-50/95 backdrop-blur-xl border-t border-background-200/60 animate-fade-in-down">
          <div className="px-4 py-4 space-y-1">
            {navLinks.map((link) => (
              <div key={link.label}>
                {link.href.startsWith('/') ? (
                  <Link
                    to={link.href}
                    onClick={() => setMobileOpen(false)}
                    className={`flex items-center justify-between px-3 py-2.5 text-sm font-medium rounded-lg transition-colors ${
                      isActive(link.href)
                        ? 'text-primary-700 bg-background-100'
                        : 'text-foreground-800 hover:text-foreground-950 hover:bg-background-100'
                    }`}
                  >
                    {link.label}
                  </Link>
                ) : (
                  <a
                    href={link.href}
                    onClick={(e) => {
                      if (!link.children) handleNavClick(e, link.href);
                    }}
                    className="flex items-center justify-between px-3 py-2.5 text-sm font-medium text-foreground-800 hover:text-foreground-950 hover:bg-background-100 rounded-lg transition-colors"
                  >
                    {link.label}
                    {link.children && <i className="ri-arrow-right-s-line text-sm" />}
                  </a>
                )}
                {link.children && (
                  <div className="pl-4 space-y-0.5">
                    {link.children.map((child) => (
                      <a
                        key={child.label}
                        href={child.href}
                        className="block px-3 py-2 text-sm text-foreground-600 hover:text-foreground-900 transition-colors"
                      >
                        {child.label}
                      </a>
                    ))}
                  </div>
                )}
              </div>
            ))}
            <div className="pt-3 border-t border-background-200/50">
              <a
                href="#contact"
                onClick={(e) => handleNavClick(e, '#contact')}
                className="flex items-center justify-center gap-1.5 px-5 py-2.5 bg-foreground-900 text-background-50 text-sm font-medium rounded-full hover:bg-foreground-800 transition-colors"
              >
                Request a Demo
                <i className="ri-arrow-right-line text-sm" />
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}